import React, { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import utcPlugin from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import TimezoneSelect from "./TimezoneSelect";
import {
  formatInTimezone,
  formatViewerTimeHint,
  isValidTimezone,
  normalizeScheduledTime,
} from "../../utils/scheduleTime";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

interface ScheduleDateTimeFieldProps {
  value: string;
  onChange: (value: string) => void;
  minNow?: boolean;
  disabled?: boolean;
  autoOpenOnMount?: boolean;
  id?: string;
  className?: string;
  /**
   * IANA zone the wall-clock should be interpreted in. Omit to keep the original
   * browser-local behaviour (blogs and any unmigrated caller).
   */
  timezone?: string;
  /** Provide to render the timezone picker. Omit for a read-only zone. */
  onTimezoneChange?: (timezone: string) => void;
  timezoneLabel?: string;
  /** SGL-501: reword the delay hint for a pending post's planned (not yet scheduled) time. */
  planned?: boolean;
}

const ScheduleDateTimeField: React.FC<ScheduleDateTimeFieldProps> = ({
  value,
  onChange,
  minNow = true,
  disabled = false,
  autoOpenOnMount = false,
  id,
  className = "",
  timezone,
  onTimezoneChange,
  timezoneLabel = "Timezone",
  planned = false,
}) => {
  const zoned = isValidTimezone(timezone);
  const tz = zoned ? (timezone as string) : undefined;

  // "Now" has to be expressed on the same clock as the picker, or minDateTime
  // would block valid times (or allow past ones) whenever the business profile's
  // zone is ahead of / behind the viewer's.
  const now = useMemo(() => (tz ? dayjs().tz(tz) : dayjs()), [tz]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [showPickedMessage, setShowPickedMessage] = useState(false);

  const selected = value ? new Date(value) : null;
  const hasValidDate = selected !== null && !Number.isNaN(selected.getTime());
  // Render the stored instant on the target zone's clock so the user edits the
  // wall-clock they originally chose, not its translation into their own zone.
  const selectedDayjs = hasValidDate
    ? tz
      ? dayjs(selected).tz(tz)
      : dayjs(selected)
    : null;

  const toDateTimeLocalValue = (d: Dayjs): string => d.format("YYYY-MM-DDTHH:mm");

  useEffect(() => {
    if (autoOpenOnMount && !disabled) {
      setPickerOpen(true);
    }
  }, [autoOpenOnMount, disabled]);

  useEffect(() => {
    if (!showPickedMessage) return;
    const timer = window.setTimeout(() => setShowPickedMessage(false), 1800);
    return () => window.clearTimeout(timer);
  }, [showPickedMessage]);

  // Changing the zone keeps the WALL-CLOCK and moves the instant: picking
  // "9:00 AM" then switching to Manila means 9:00 AM in Manila. That matches
  // what the control reads as — the alternative (keeping the instant and
  // changing the displayed time) makes the number the user just typed jump.
  const handleTimezoneChange = (nextTz: string) => {
    if (!onTimezoneChange) return;
    if (hasValidDate && selectedDayjs) {
      const wallClock = toDateTimeLocalValue(selectedDayjs);
      onChange(normalizeScheduledTime(wallClock, nextTz));
    }
    onTimezoneChange(nextTz);
  };

  const resolvedHint = hasValidDate && tz ? formatInTimezone(selected!.toISOString(), tz) : "";
  const viewerHint = hasValidDate && tz ? formatViewerTimeHint(selected!.toISOString(), tz) : "";

  return (
    <div className={className}>
      <div className="rounded-xl border border-theme-border bg-theme-background p-3">
        {pickerOpen && !disabled && (
          <div className="mb-2 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-theme-border bg-theme-surface px-2.5 py-1 text-xs font-medium text-theme-fg hover:bg-theme-secondary/10"
              onClick={() => setPickerOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-brand-primary px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-primary-hover"
              onClick={() => {
                setPickerOpen(false);
                if (hasValidDate) setShowPickedMessage(true);
              }}
            >
              OK / Save
            </button>
          </div>
        )}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            value={selectedDayjs}
            onChange={(newValue) => {
              if (!newValue || !newValue.isValid()) {
                onChange("");
                return;
              }
              // The picker hands back a wall-clock; resolve it against the
              // target zone so the stored instant reflects the client's clock.
              onChange(normalizeScheduledTime(toDateTimeLocalValue(newValue), tz));
            }}
            open={pickerOpen}
            onOpen={() => setPickerOpen(true)}
            onClose={() => setPickerOpen(false)}
            disabled={disabled}
            enableAccessibleFieldDOMStructure={false}
            minDateTime={minNow ? now : undefined}
            slotProps={{
              textField: {
                id,
                size: "small",
                placeholder: "Pick date and time",
                onClick: () => setPickerOpen(true),
                fullWidth: true,
              },
              popper: {
                sx: { zIndex: 1600 },
              },
            }}
            sx={{
              width: "100%",
              "& .MuiInputBase-root": {
                borderRadius: "0.5rem",
                backgroundColor: "var(--color-theme-surface)",
                color: "var(--color-theme-fg)",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--color-theme-border)",
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--color-theme-primary)",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--color-theme-primary)",
              },
            }}
          />
        </LocalizationProvider>

        {onTimezoneChange && (
          <TimezoneSelect
            id={id ? `${id}-timezone` : undefined}
            className="mt-2"
            value={tz ?? ""}
            onChange={handleTimezoneChange}
            disabled={disabled}
          />
        )}
      </div>

      {resolvedHint && (
        <p
          data-testid="schedule-hint"
          className="mt-1.5 text-[11px] text-theme-fg-muted"
        >
          {resolvedHint}
          {viewerHint && (
            <span className="opacity-70"> · You: {viewerHint}</span>
          )}
        </p>
      )}

      <p className="mt-2 rounded-md border border-amber-400/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-700">
        {planned
          ? "Planned posts go out once approved; publishing may be delayed. Estimated delay: 15-30 minutes."
          : "Scheduled posts may be delayed. Estimated delay: 15-30 minutes."}
      </p>
      {showPickedMessage && (
        <p className="mt-1 rounded-md border border-emerald-400/30 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-700">
          Date and time selected.
        </p>
      )}
    </div>
  );
};

export default ScheduleDateTimeField;
